/**
 * One window of the Recorded wall: a camera's recording, held to the wall's clock.
 *
 * The tile never decides WHAT time it is — the clock does. Four times a second it asks "what
 * should be on screen now" through its own offset table and makes the <video> agree:
 *
 *   clock in a gap for this camera  → pause, say "No recording at 12:10:27"
 *   off by more than DRIFT_SEEK_S   → seek (an explicit seek on the wall seeks at once)
 *   off by a little                 → nudge playbackRate ±10% until it lines up — a seek per
 *                                     half-second of drift would stutter every tile
 *   clock advancing and data ready  → play; otherwise pause
 *
 * and reports its state up, which is how the clock knows to hold for a tile that is loading.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { XMarkIcon, VideoCameraIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import RecordingPlaybackPlayer from '../recordings/RecordingPlaybackPlayer';
import { buildOffsetTable, footageAt, nextFootageAfter, wallToPlayer, type OffsetRow } from '../recordings/playbackOffsets';
import type { RecordingSegment } from '../../api/contracts';
import type { WallWindow } from '../live-wall/wallLayout';
import { gridArea } from '../live-wall/wallLayout';
import type { PlaybackClock } from './usePlaybackClock';
import { Tip } from '../common/Tooltip';

export type TileStatus =
  | 'empty'          // no camera in the window
  | 'segments'       // fetching this camera's segments
  | 'no-footage'     // nothing recorded in the range at all
  | 'gap'            // footage exists, just not at the clock's time
  | 'loading'        // playlist / first chunk not in yet
  | 'buffering'
  | 'ready'
  | 'error';

/** The drag payload types. Custom MIME types so an unrelated drag (a file, text) is ignored. */
export const DND_CAMERA = 'application/x-trinetra-camera';
export const DND_WINDOW = 'application/x-trinetra-wall-window';

const DRIFT_SEEK_S = 1.5;
const DRIFT_NUDGE_S = 0.25;
const FOLLOW_MS = 250;

const fmtTime = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

export interface PlaybackWallTileProps {
  window: WallWindow;
  camera: { cameraId: string; cameraName: string } | null;
  /** undefined = still fetching. */
  segments: RecordingSegment[] | undefined;
  rangeStartMs: number;
  rangeEndMs: number;
  /** False while the page may still narrow the range to the footage. The playlist is built
   *  for one exact range, so building it before then fetches it twice. Defaults true. */
  rangeSettled?: boolean;
  clock: PlaybackClock;
  playbackUrl: (cameraId: string, fromIso: string, toIso: string) => string | null;
  getAuthToken: () => string | null;
  tunnelled: boolean;
  /** Lane colour — the same one the timeline draws this camera's lane in. */
  accent?: string;
  onStatus: (position: number, status: TileStatus) => void;
  onDropCamera: (position: number, camera: { id: string; name: string }) => void;
  onDropWindow: (from: number, to: number) => void;
  onRemove: (position: number) => void;
}

function PlaybackWallTile({
  window: w,
  camera,
  segments,
  rangeStartMs,
  rangeEndMs,
  rangeSettled = true,
  clock,
  playbackUrl,
  getAuthToken,
  tunnelled,
  accent,
  onStatus,
  onDropCamera,
  onDropWindow,
  onRemove,
}: PlaybackWallTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<TileStatus>(camera ? 'segments' : 'empty');
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const rows = useMemo<OffsetRow[]>(
    () => (segments ? buildOffsetTable(segments, rangeStartMs, rangeEndMs) : []),
    [segments, rangeStartMs, rangeEndMs],
  );
  const hasFootage = rows.length > 0;

  // One playlist per camera + range. Built only when there is something to play: asking the
  // node for an empty range's playlist is a round trip (through the tunnel, on the cloud) for a 404.
  const src = useMemo(() => {
    if (!camera || !hasFootage || !rangeSettled) return null;
    return playbackUrl(camera.cameraId, new Date(rangeStartMs).toISOString(), new Date(rangeEndMs).toISOString());
    // retryKey: a Retry rebuilds the source, which is what re-runs the player's setup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera?.cameraId, hasFootage, rangeSettled, rangeStartMs, rangeEndMs, playbackUrl, retryKey]);

  // Where the wall clock is when this source is built, in player-time — handed to hls.js as
  // startPosition so its FIRST fragment is the one on screen. Without it hls.js fetched
  // fragment 0 (the start of the range, usually midnight) before the follow loop could seek:
  // on an H265 camera that is a whole wasted transcode per tile, and with the playback
  // service's small transcode cap the tiles queued behind each other for ~10s.
  const startPosition = useMemo(() => {
    const t = clock.timeRef.current;
    return src && footageAt(rows, t) ? wallToPlayer(rows, t) : undefined;
    // Only per source: later clock movement is the follow loop's job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // ─── Refs the follow loop reads, so it is set up once per source ───
  const rowsRef = useRef(rows);
  const advancingRef = useRef(clock.advancing);
  const rateRef = useRef(clock.rate);
  const seekPendingRef = useRef(true);
  const loadedOnceRef = useRef(false);
  const errorRef = useRef<string | null>(null);
  const statusRef = useRef<TileStatus>(status);
  useEffect(() => { rowsRef.current = rows; seekPendingRef.current = true; }, [rows]);
  useEffect(() => { advancingRef.current = clock.advancing; }, [clock.advancing]);
  useEffect(() => { rateRef.current = clock.rate; }, [clock.rate]);
  useEffect(() => { errorRef.current = error; }, [error]);
  // An explicit seek on the wall — jump now, don't wait to notice the drift.
  useEffect(() => { seekPendingRef.current = true; }, [clock.seekSeq]);
  useEffect(() => { loadedOnceRef.current = false; seekPendingRef.current = true; setError(null); }, [src]);

  const report = useCallback((s: TileStatus) => {
    if (statusRef.current === s) return;
    statusRef.current = s;
    setStatus(s);
    onStatus(w.position, s);
  }, [onStatus, w.position]);

  // A camera moved to another window (same tile, new position) says where it is now — the page
  // keys its hold on window positions, and an unchanged status would never be re-sent.
  useEffect(() => { onStatus(w.position, statusRef.current); }, [w.position, onStatus]);

  // Whatever changes the answer before the loop runs (camera removed, segments arriving).
  useEffect(() => {
    if (!camera) report('empty');
    else if (!segments) report('segments');
    else if (!hasFootage) report('no-footage');
    else if (!rangeSettled) report('segments');  // the rest of the wall is still arriving
    else if (!src) {
      // Footage exists but the host cannot address it — the cloud with its session gone.
      setError('Cannot open this recording — try signing in again.');
      report('error');
    }
  }, [camera, segments, hasFootage, rangeSettled, src, report]);

  // ─── The follow loop ───
  const timeRef = clock.timeRef;
  useEffect(() => {
    if (!src) return undefined;
    const id = window.setInterval(() => {
      const v = videoRef.current;
      const table = rowsRef.current;
      if (!v || !table.length) return;
      if (errorRef.current) { report('error'); return; }

      const t = timeRef.current;
      if (!footageAt(table, t)) {
        if (!v.paused) v.pause();
        report('gap');
        return;
      }
      if (v.readyState < 1) { report(loadedOnceRef.current ? 'buffering' : 'loading'); return; }

      const target = wallToPlayer(table, t);
      const drift = v.currentTime - target;
      if ((seekPendingRef.current || Math.abs(drift) > DRIFT_SEEK_S) && !v.seeking) {
        seekPendingRef.current = false;
        try { v.currentTime = target; } catch { /* not seekable yet — next tick */ }
      }

      const ready = v.readyState >= 3 && !v.seeking;
      if (ready) loadedOnceRef.current = true;
      report(ready ? 'ready' : loadedOnceRef.current ? 'buffering' : 'loading');

      if (advancingRef.current) {
        const nudge = Math.abs(drift) > DRIFT_NUDGE_S && Math.abs(drift) <= DRIFT_SEEK_S
          ? Math.max(-0.1, Math.min(0.1, -drift * 0.1))
          : 0;
        const r = rateRef.current * (1 + nudge);
        if (Math.abs(v.playbackRate - r) > 0.001) v.playbackRate = r;
        if (v.paused && ready) v.play().catch(() => { /* policy — muted, so rare */ });
      } else if (!v.paused) {
        v.pause();
      }
    }, FOLLOW_MS);
    return () => window.clearInterval(id);
  }, [src, report, timeRef]);

  // ─── Drag & drop ───
  const acceptsDrag = (e: DragEvent) =>
    e.dataTransfer.types.includes(DND_CAMERA) || e.dataTransfer.types.includes(DND_WINDOW);

  const onDragOver = (e: DragEvent) => {
    if (!acceptsDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = e.dataTransfer.types.includes(DND_WINDOW) ? 'move' : 'copy';
    if (!dragOver) setDragOver(true);
  };
  const onDrop = (e: DragEvent) => {
    setDragOver(false);
    const cam = e.dataTransfer.getData(DND_CAMERA);
    const from = e.dataTransfer.getData(DND_WINDOW);
    if (!cam && !from) return;
    e.preventDefault();
    if (cam) {
      try {
        const parsed = JSON.parse(cam);
        if (parsed?.id) onDropCamera(w.position, { id: String(parsed.id), name: String(parsed.name || parsed.id) });
      } catch { /* not ours */ }
    } else if (from !== '' && Number(from) !== w.position) {
      onDropWindow(Number(from), w.position);
    }
  };
  const onDragStart = (e: DragEvent) => {
    e.dataTransfer.setData(DND_WINDOW, String(w.position));
    e.dataTransfer.effectAllowed = 'move';
  };

  const ring = dragOver ? 'ring-2 ring-brand ring-offset-1 ring-offset-background' : '';

  if (!camera) {
    return (
      <div
        style={gridArea(w)}
        onDragOver={onDragOver}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative flex items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
          dragOver ? 'border-brand bg-brand-lighter' : 'border-border'
        }`}
      >
        <div className="flex flex-col items-center gap-1.5 px-2 text-center text-muted-foreground">
          <VideoCameraIcon className="h-6 w-6 sm:h-8 sm:w-8" />
          <span className="text-[10px] font-medium sm:text-xs">Drag a camera here</span>
        </div>
      </div>
    );
  }

  const clockMs = clock.time;
  const next = status === 'gap' ? nextFootageAfter(rows, clockMs) : null;

  return (
    <div
      style={gridArea(w)}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`group relative overflow-hidden rounded-lg bg-black ${ring}`}
    >
      {src && (
        <RecordingPlaybackPlayer
          key={`${src}|${retryKey}`}
          src={src}
          startPosition={startPosition}
          videoRef={videoRef}
          wantPlaying={clock.advancing}
          playbackRate={clock.rate}
          muted
          tunnelled={tunnelled}
          getAuthToken={getAuthToken}
          onError={setError}
        />
      )}

      {/* No date/time overlay on the video: the camera burns its own into the picture, and the
          wall's one time is in the transport readout. */}
      {/* Name — bottom left, with the lane colour so tile and timeline row pair up at a glance. */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 flex items-center gap-1.5 bg-gradient-to-t from-black/80 to-transparent px-2 pb-1.5 pt-4">
        {accent && <span className="h-2 w-2 flex-none rounded-full" style={{ background: accent }} />}
        <span className="truncate text-[11px] font-semibold text-white sm:text-xs">{camera.cameraName}</span>
      </div>

      <Tip side="left" label={`Take ${camera.cameraName} off the wall`} className="absolute right-1.5 top-1.5 z-10">
        <button
          onClick={() => onRemove(w.position)}
          className="rounded-md bg-black/60 p-1 text-white opacity-0 transition-opacity hover:bg-red-500/80 focus:opacity-100 group-hover:opacity-100"
          aria-label="Remove camera from window"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      </Tip>

      {/* ─── States ─── */}
      {(status === 'segments' || status === 'loading') && (
        <Overlay>
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          <span>{status === 'segments' ? 'Finding recordings…' : 'Loading recording…'}</span>
        </Overlay>
      )}
      {status === 'buffering' && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="rounded bg-black/60 px-2 py-1 text-[11px] text-white">Buffering…</div>
        </div>
      )}
      {status === 'no-footage' && (
        <Overlay>
          <VideoCameraIcon className="h-6 w-6 opacity-60" />
          <span>No recordings in this time range</span>
        </Overlay>
      )}
      {status === 'gap' && (
        <Overlay>
          <span>No recording at {fmtTime(clockMs)}</span>
          {next && <span className="text-[10px] text-white/60">resumes at {fmtTime(next.wallStart)}</span>}
        </Overlay>
      )}
      {status === 'error' && (
        <Overlay>
          <ExclamationTriangleIcon className="h-6 w-6 text-amber-400" />
          <span className="max-w-[90%]">{error}</span>
          <button
            onClick={() => { setError(null); setRetryKey((k) => k + 1); }}
            className="pointer-events-auto mt-1 flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-[11px] text-white hover:bg-white/20"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" /> Retry
          </button>
        </Overlay>
      )}
    </div>
  );
}

function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/70 px-3 text-center text-[11px] text-white sm:text-xs">
      {children}
    </div>
  );
}

export default memo(PlaybackWallTile);
